/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, ICustomProperties, IDependencyTelemetry, IEventTelemetry, IExceptionTelemetry, IMetricTelemetry, IPageViewTelemetry, ITelemetryPlugin, ITraceTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { AppConfig } from '../models/AppConfig';

@Injectable()
export class TelemetryService {
    private angularPlugin = new AngularPlugin();
    private _authenticatedUserId: string | null = null;
    private _appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: AppConfig.GLOBAL_CONFIG.applicationInsightsKey,
            extensions: [this.angularPlugin as unknown as ITelemetryPlugin],
            //auto router tracking, default pageview duration will be set to 0
            extensionConfig: {
                [this.angularPlugin.identifier]: {
                    router: this.router,
                },
            },
        },
    });

    constructor(private router: Router, private _msalService: MsalService) {
        this._appInsights.loadAppInsights();
        this.setAuthenticatedUserContext(this.getAccountEmail(this._msalService.instance.getActiveAccount()));
        this._appInsights.addTelemetryInitializer(envelope => {
            envelope.tags = envelope.tags || [];
            if (window.parent !== window) {
                envelope.tags['ai.cloud.role'] = 'frameinbrowser';
            } else {
                envelope.tags['ai.cloud.role'] = 'browser';
            }
            envelope.tags['ai.application.ver'] = '2.1.2.7';
            if (this._authenticatedUserId) {
                envelope.tags['ai.user.authUserId'] = this._authenticatedUserId;
                const baseData = envelope.baseData as { properties?: Record<string, string> } | undefined;
                if (baseData) {
                    baseData.properties = baseData.properties || {};
                    baseData.properties['userEmail'] = this._authenticatedUserId;
                }
            }
            try {
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('0 Unknown Error') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('InteractionRequiredAuthError: interaction_required') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('Error: could not resolve endpoint') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('redirect_in_iframe') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('interaction_in_progress') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('BrowserAuthError') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('ClientAuthError') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('AADB2C90157') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('AADB2C90091') > -1) { // user has cancelled entering self-asserted
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('RangeError') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('"status":504') > -1) {
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                // if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('NG04002') > -1) { // when a user had a google browser agent, used for search in ios 
                //     this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                //     return false;
                // }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('An unexpected server error occurred') > -1) { // i think this may happen when user closes browser or network issue? 
                    this._appInsights.trackTrace({ message: envelope?.baseData?.exceptions[0]?.message, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                if (envelope.baseType === 'ExceptionData' && envelope?.baseData?.exceptions[0]?.message?.indexOf('EmptyError') > -1) {
                    //happens during login after token expired
                    return false;
                }
                if (envelope.baseType === 'Ajax') {
                    const ajaxData = JSON.stringify(envelope);
                    this._appInsights.trackTrace({ message: `Ajax status ${ajaxData}`, severityLevel: SeverityLevel.Warning });
                    return false;
                }
                return true;
            } catch (error) {
                this._appInsights.trackTrace({ message: 'error in telemetry initializer', severityLevel: SeverityLevel.Warning });
                return true;
            }
        });
    }

    setAuthenticatedUserContext(userEmail: string | null) {
        this._authenticatedUserId = userEmail;
        if (userEmail) {
            this._appInsights.setAuthenticatedUserContext(userEmail);
            return;
        }
        this._appInsights.clearAuthenticatedUserContext();
    }

    private getAccountEmail(account: AccountInfo | null): string | null {
        if (!account) {
            return null;
        }

        const claims: any = account.idTokenClaims || {};
        return (claims.emails && claims.emails[0])
            || claims.email
            || claims.preferred_username
            || claims.upn
            || claims.login_hint
            || claims['signInNames.emailAddress']
            || claims['signInNames.email']
            || claims.signInNames?.emailAddress
            || account.username
            || null;
    }

    // expose tracking methods
    trackEvent(event: IEventTelemetry, customProperties?: ICustomProperties) {
        this._appInsights.trackEvent(event, customProperties);
    }

    startTrackEvent(name?: string) {
        this._appInsights.startTrackEvent(name);
    }

    stopTrackEvent(name: string, properties?: { [p: string]: string }, measurements?: { [p: string]: number }): any {
        this._appInsights.stopTrackEvent(name, properties, measurements);
    }

    trackPageView(pageView?: IPageViewTelemetry) {
        this._appInsights.trackPageView(pageView);
    }

    startTrackPage(name?: string) {
        this._appInsights.startTrackPage(name);
    }

    stopTrackPage(name?: string, url?: string, properties?: { [name: string]: string }, measurements?: { [name: string]: number }) {
        this._appInsights.stopTrackPage(name, url, properties, measurements);
    }

    trackMetric(metric: IMetricTelemetry, properties?: ICustomProperties) {
        this._appInsights.trackMetric(metric, properties);
    }

    trackException(exception: IExceptionTelemetry, properties?: ICustomProperties) {
        this._appInsights.trackException(exception, properties);
    }

    trackTrace(message: ITraceTelemetry, properties?: ICustomProperties) {
        this._appInsights.trackTrace(message, properties);
    }

    trackDependencyData(dependency: IDependencyTelemetry) {
        this._appInsights.trackDependencyData(dependency);
    }

    flush() {
        this._appInsights.flush();
    }
}
